import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getChatAgentUrl, getChatServiceUrl } from '../utils/backend'
import { getSessionToken } from '../utils/sessionAuth'
import { listChats, getChatTranscript, archiveChat } from '../api/chat'
import { getSessionUserId } from '../utils/sessionUser'
import {
  formatUtcDateLabel,
  getBrowserTimeZone,
  utcStringToLocalDate,
  utcStringToTimestamp,
} from '../utils/timezone'
import Loader from '../components/Loading'
import ReactMarkdown from 'react-markdown'
import { FiChevronRight } from 'react-icons/fi'
import Hero from '../components/chat/Hero'
import '../style/views/AssistantView.css'

type Message = {
  role: 'user' | 'assistant'
  content: string
  id: string
}

type PendingAction = {
  category: string
  action: string
  id: string | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO(ENG-399): replace with proper type
  data: Record<string, any>
}

type ChatHistory = {
  id: string
  title: string
  lastMessage: string
  date: string
  rawDate?: string
}

const toChatHistory = (item: {
  id: string
  title?: string | null
  date_created?: string
}): ChatHistory => ({
  id: item.id,
  title: item.title || 'Previous Chat',
  lastMessage: '',
  date: formatUtcDateLabel(item.date_created),
  rawDate: item.date_created || '',
})

const sortChatHistory = (items: ChatHistory[]) =>
  items.toSorted((a, b) => utcStringToTimestamp(b.rawDate) - utcStringToTimestamp(a.rawDate))

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO(ENG-399): replace with proper type
function formatActionValue(key: string, value: any) {
  if (value === null || value === '') return '';
  const valStr = String(value);

  const isDateKey = key.toLowerCase().includes('date') || key.toLowerCase().includes('time');
  const isDateVal = /^\d{4}-\d{2}-\d{2}/.test(valStr);

  if (isDateKey && isDateVal) {
    try {
      const d = utcStringToLocalDate(valStr);
      if (d && !isNaN(d.getTime())) {
        return `${d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })} ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).replace(/\s*([AP]M)$/i, (_, m) => m.toLowerCase())}`;
      }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- intentional catch-all
    } catch (_e) {
      return valStr;
    }
  }
  return valStr;
}

function AssistantView() {
  const navigate = useNavigate()
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showHistory, setShowHistory] = useState(true)
  const [isConnected, setIsConnected] = useState(false)
  const [chatSessions, setChatSessions] = useState<ChatHistory[]>([])
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null)
  const [isNewChatActive, setIsNewChatActive] = useState(true)
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([])
  const [isDismissing, setIsDismissing] = useState(false)
  const [calendarConnectRequired, setCalendarConnectRequired] = useState(false)
  const [currentStatus, setCurrentStatus] = useState('')
  const [searchParams] = useSearchParams()
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const wsRef = useRef<WebSocket | null>(null)

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to calculate scrollHeight correctly
    textarea.style.height = '46px'; // Baseline height
    const newHeight = Math.max(46, Math.min(textarea.scrollHeight, 400));
    textarea.style.height = `${newHeight}px`;
  }, [input]);

  // Handle direct navigation via ID
  useEffect(() => {
    const id = searchParams.get('id')
    if (id && id !== activeSessionId) {
      loadSession(id)
    }
  }, [searchParams])
  const [displayedContent, setDisplayedContent] = useState('')
  const fullContentRef = useRef('')
  const isStreamingRef = useRef(false)
  const animationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Word-by-word drip animation
  useEffect(() => {
    if (animationTimerRef.current) clearInterval(animationTimerRef.current)
    
    animationTimerRef.current = setInterval(() => {
      setDisplayedContent(prev => {
        if (prev.length >= fullContentRef.current.length) {
          // If loading is done and we caught up, stop the timer
          if (!isStreamingRef.current && animationTimerRef.current) {
            clearInterval(animationTimerRef.current)
            animationTimerRef.current = null
          }
          return prev
        }
        
        // Find the next space to complete a word
        const remaining = fullContentRef.current.slice(prev.length)
        const nextSpace = remaining.indexOf(' ', 1)
        
        if (nextSpace === -1) {
          // If no more spaces, and still loading, wait. If done loading, show all.
          return isStreamingRef.current ? prev : fullContentRef.current
        }
        
        return fullContentRef.current.slice(0, prev.length + nextSpace + 1)
      })
    }, 25) // Faster word speed (25ms per word)

    return () => {
      if (animationTimerRef.current) clearInterval(animationTimerRef.current)
    }
  }, [isLoading]) 

  // Fetch Chat History using the new /chat/list/ endpoint
  const loadHistory = useCallback(async () => {
    try {
      const userId = getSessionUserId()
      if (!userId) return
      
      const data = await listChats(userId)
      setChatSessions(sortChatHistory((data || []).map(toChatHistory)))
    } catch (err) {
      console.error('Failed to load history', err)
    }
  }, [])

  useEffect(() => {
    loadHistory()
  }, [loadHistory])

  const scrollToBottom = useCallback(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, pendingActions, scrollToBottom])

  // WebSocket Connection Management
  const connectWs = useCallback((sessionId?: string | null) => {
    const userId = getSessionUserId()
    const token = getSessionToken()
    if (!userId) return

    // Close existing connection if any
    if (wsRef.current) {
      wsRef.current.close()
    }

    const base = getChatAgentUrl().replace(/^http/, 'ws')
    const browserTz = getBrowserTimeZone()
    let wsUrl = `${base}/chat/?user_id=${encodeURIComponent(userId)}${token ? `&token=${encodeURIComponent(token)}` : ''}`
    if (browserTz) {
      wsUrl += `&timezone=${encodeURIComponent(browserTz)}`
    }
    
    // Pass the session ID to continue an existing chat
    if (sessionId) {
      wsUrl += `&id=${encodeURIComponent(sessionId)}`
    }
    
    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    ws.onopen = () => setIsConnected(true)
    ws.onclose = () => {
      setIsConnected(false)
      // Removed manual loadHistory timeout as SSE handles this now
    }
    ws.onerror = (err) => console.error('WebSocket Error:', err)

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        
        if (data.type === 'start') {
          setIsLoading(true)
          setCurrentStatus('')
          isStreamingRef.current = true
          fullContentRef.current = ''
          setDisplayedContent('')
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: '',
            id: `msg-${Date.now()}`
          }])
        } else if (data.type === 'delta') {
          fullContentRef.current += (data.text || '')
        } else if (data.type === 'done') {
          isStreamingRef.current = false
          // Final sync of the message content state
          setMessages(prev => {
            const last = prev[prev.length - 1]
            if (last && last.role === 'assistant') {
              return [
                ...prev.slice(0, -1),
                { ...last, content: fullContentRef.current }
              ]
            }
            return prev
          })
          setIsLoading(false)
        } else if (data.type === 'status') {
          setCurrentStatus(data.text || '')
        } else if (data.type === 'pending_actions') {
          setPendingActions(data.actions || [])
          setIsDismissing(false)
          setIsLoading(false)
        } else if (data.type === 'action_complete') {
          setIsDismissing(true)
          setCalendarConnectRequired(false)
          setTimeout(() => {
            setPendingActions([])
            setIsDismissing(false)
          }, 400)
          setIsLoading(false)
          console.log(`${data.action} successful for ${data.category}`)
        } else if (data.type === 'action_cancelled') {
          setIsDismissing(true)
          setCalendarConnectRequired(false)
          setTimeout(() => {
            setPendingActions([])
            setIsDismissing(false)
          }, 400)
          setIsLoading(false)
        } else if (data.type === 'calendar_connect_required') {
          setCalendarConnectRequired(true)
          setPendingActions([])
          setIsDismissing(false)
          setIsLoading(false)
        } else if (data.type === 'chat_saved' && data.id) {
          const mapped = toChatHistory({
            id: data.id,
            title: data.title,
            date_created: data.date_created,
          })
          setActiveSessionId(mapped.id)
          setIsNewChatActive(false)
          setChatSessions(prev => {
            const index = prev.findIndex(s => s.id === mapped.id)
            const next = index === -1 ? [mapped, ...prev] : [...prev.slice(0, index), mapped, ...prev.slice(index + 1)]
            return sortChatHistory(next)
          })
        }
      } catch (err) {
        console.error('Failed to parse WS message', err)
      }
    }
  }, []) // loadHistory removed from dependencies as we don't call it here anymore

  useEffect(() => {
    connectWs()
    return () => {
      wsRef.current?.close()
    }
  }, [connectWs])

  // Real-time Chat Stream (SSE) Integration
  useEffect(() => {
    const userId = getSessionUserId()
    if (!userId) return

    const token = getSessionToken()
    const params = new URLSearchParams({ user_id: userId })
    if (token) params.set('token', token)
    const url = `${getChatServiceUrl()}/chat/stream/?${params.toString()}`
    let eventSource: EventSource | null = null
    let cancelled = false

    const connect = async () => {
      try {
        const controller = new AbortController()
        const timeout = window.setTimeout(() => controller.abort(), 5000)
        const response = await fetch(url, {
          headers: { Accept: 'text/event-stream' },
          signal: controller.signal,
        })
        window.clearTimeout(timeout)
        if (!response.ok) {
          let body = ''
          try {
            body = await response.text()
          } catch {
            body = ''
          }
          console.error('SSE stream rejected', {
            status: response.status,
            statusText: response.statusText,
            body,
          })
          return
        }
        controller.abort()
      } catch (err) {
        if ((err as DOMException).name !== 'AbortError') {
          console.error('SSE preflight failed', err)
        }
      }

      if (cancelled) return
      eventSource = new EventSource(url)

      eventSource.addEventListener('chat-update', (event) => {
        try {
          const updatedChat = JSON.parse(event.data)
          const mapped = toChatHistory(updatedChat)

          setChatSessions(prev => {
            const index = prev.findIndex(s => s.id === mapped.id)
            const newList = index !== -1
              ? prev.with(index, mapped)
              : [mapped, ...prev]
            return sortChatHistory(newList)
          })
        } catch (err) {
          console.error('Failed to parse SSE chat-update', err)
        }
      })

      eventSource.onerror = (err) => {
        console.error('SSE connection failed', err)
        void loadHistory()
      }
    }

    void connect()

    return () => {
      cancelled = true
      eventSource?.close()
    }
  }, [loadHistory])

  const performSend = () => {
    if (!input.trim()) return

    // If there were pending actions, close/deny them since the user is sending a new message
    if (pendingActions.length > 0) {
      setIsDismissing(true)
      setTimeout(() => {
        setPendingActions([])
        setIsDismissing(false)
      }, 400)
      wsRef.current?.send(JSON.stringify({ type: 'deny_action' }))
    }
    setCalendarConnectRequired(false)

    const userMessage: Message = {
      role: 'user',
      content: input.trim(),
      id: `msg-${Date.now()}`,
    }

    setMessages((prev) => [...prev, userMessage])
    wsRef.current?.send(JSON.stringify({ text: userMessage.content }))
    setInput('')
  }

  const handleSend = async (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!input.trim()) return

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      connectWs(activeSessionId)
      setTimeout(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          performSend()
        }
      }, 500)
      return
    }

    performSend()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setCalendarConnectRequired(false)

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      connectWs(activeSessionId)
      setTimeout(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          const userMessage: Message = {
            role: 'user',
            content: suggestion,
            id: `msg-${Date.now()}`,
          }
          setMessages((prev) => [...prev, userMessage])
          wsRef.current?.send(JSON.stringify({ text: suggestion }))
        }
      }, 500)
      return
    }

    const userMessage: Message = {
      role: 'user',
      content: suggestion,
      id: `msg-${Date.now()}`,
    }
    setMessages((prev) => [...prev, userMessage])
    wsRef.current?.send(JSON.stringify({ text: suggestion }))
  }

  const handleNewChat = () => {
    setMessages([])
    setInput('')
    setIsLoading(false)
    setIsDismissing(true)
    setTimeout(() => {
      setPendingActions([])
      setCalendarConnectRequired(false)
      setIsDismissing(false)
    }, 400)
    setActiveSessionId(null)
    setIsNewChatActive(true)
    connectWs(null)
  }

  // Action handlers
  const approveAction = (action: PendingAction) => {
    wsRef.current?.send(JSON.stringify({
      type: 'approve_action',
      action: action
    }))
  }

  const denyAction = () => {
    setIsDismissing(true)
    setTimeout(() => {
      setPendingActions([])
      setIsDismissing(false)
    }, 400)
    wsRef.current?.send(JSON.stringify({
      type: 'deny_action'
    }))
  }

  // Load an existing session's transcript and reconnect WebSocket for continuation
  const loadSession = async (sessionId: string) => {
    if (activeSessionId === sessionId) return
    
    try {
      const userId = getSessionUserId()
      if (!userId) return
      
      setIsLoading(true)
      setIsNewChatActive(false)
      setActiveSessionId(sessionId)
      setPendingActions([])
      setCalendarConnectRequired(false)
      
      // Clear current messages while loading new ones to prevent "flash" or "weirdness"
      setMessages([])

      const transcript = await getChatTranscript(sessionId, userId)
      if (transcript) {
        const parts = transcript.split(/(User:|Assistant:)/i)
        const parsedMessages: Message[] = []
        const userIndicators = new Set(['user:'])

        for (let i = 1; i < parts.length; i += 2) {
          const roleIndicator = parts[i].toLowerCase()
          const role = userIndicators.has(roleIndicator) ? 'user' : 'assistant'
          const content = parts[i + 1]?.trim() || ''
          
          if (content) {
            parsedMessages.push({
              role,
              content,
              id: `loaded-${sessionId}-${i}`
            })
          }
        }

        if (parsedMessages.length > 0) {
          setMessages(parsedMessages)
        } else {
          setMessages([{ role: 'assistant', content: transcript || 'No content found.', id: sessionId }])
        }

        // Re-establish WebSocket connection with the session ID for context rehydration
        connectWs(sessionId)
      } else {
        setMessages([{ role: 'assistant', content: 'No content found.', id: sessionId }])
        connectWs(sessionId)
      }
    } catch (err) {
      console.error('Failed to load session', err)
      setMessages([{ role: 'assistant', content: 'Error loading session content.', id: 'error' }])
    } finally {
      setIsLoading(false)
    }
  }

  const handleArchiveSession = async (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation()
    try {
      const userId = getSessionUserId()
      if (!userId) return
      await archiveChat(sessionId, userId)
      setChatSessions(prev => prev.filter(s => s.id !== sessionId))
      if (activeSessionId === sessionId) handleNewChat()
    } catch (err) {
      console.error('Archive failed', err)
    }
  }

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [])

  return (
    <div className="assistant-layout">
      <aside className={`assistant-sidebar-wrap ${showHistory ? 'open' : 'closed'}`}>
        <button
          type="button"
          className="sidebar-collapse-toggle"
          aria-label={showHistory ? "Collapse history" : "Expand history"}
          onClick={() => setShowHistory(true)}
          disabled={showHistory}
          aria-hidden={showHistory}
        >
          <FiChevronRight size={24} />
        </button>

        <div className="assistant-sidebar">
          <div className="assistant-history-title-row">
            <div className="assistant-sidebar-header">
              <button
                type="button"
                className="ghost-button small back-nav"
                onClick={() => navigate(-1)}
              >
                ← Back
              </button>
              <button
                className="sidebar-minus-btn"
                aria-label="Hide history"
                onClick={() => setShowHistory(false)}
              >
                –
              </button>
            </div>
          </div>
          <div className="assistant-history-content">
            <span className="history-header-title">History</span>
            <div className="history-scroll-container">
              <div className="history-list">
                {isNewChatActive && (
                  <button type="button" 
                    className={`history-item active`}
                    onClick={handleNewChat}
                  >
                    <span className="history-dot" />
                    <div className="history-info">
                      <div className="history-title">New Chat</div>
                      <div className="history-meta">{isConnected ? 'Active now' : 'Start session'}</div>
                    </div>
                  </button>
                )}
                {chatSessions.map(item => (
                  <button type="button" 
                    key={item.id} 
                    className={`history-item ${activeSessionId === item.id ? 'active' : ''}`}
                    onClick={() => loadSession(item.id)}
                  >
                    <span className={`history-dot ${activeSessionId === item.id ? 'active' : 'archived'}`} />
                    <div className="history-info">
                      <div className="history-title">{item.title}</div>
                      <div className="history-meta">{item.date}</div>
                    </div>
                    <span className="history-archive-x" onClick={(e) => handleArchiveSession(e, item.id)}>×</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="sidebar-footer-actions">
            <button type="button" 
              className="primary-outline small full-width"
              onClick={handleNewChat}
            >
              + New Chat
            </button>
          </div>
        </div>
      </aside>

      <main className="assistant-main-chat">
        <div className="assistant-chat-window">
          <div className="assistant-messages" ref={scrollRef}>
            {isLoading && messages.length === 0 ? (
              <div className="assistant-centered-loader">
                <Loader />
              </div>
            ) : messages.length === 0 ? (
              <Hero onSuggestionClick={handleSuggestionClick} />
            ) : (
              <>
                {messages.map((msg, idx) => {
                  const isLast = idx === messages.length - 1
                  const isStreaming = msg.role === 'assistant' && isLoading && isLast
                  const content = isStreaming ? displayedContent : msg.content
                  
                  return (
                    <div key={msg.id} className={`message-row ${msg.role}`}>
                      <div className={`message-bubble ${isStreaming ? 'is-streaming' : ''}`}>
                        <ReactMarkdown>{content}</ReactMarkdown>
                      </div>
                    </div>
                  )
                })}

                {pendingActions.length > 0 && (
                  <div className="message-row assistant">
                    <div className={`message-bubble action-review-card ${isDismissing ? 'dismissing' : ''}`}>
                      <h4 className="action-review-title">Review & Approve</h4>
                      <p className="action-review-subtitle">The agent wants to perform the following actions:</p>
                      
                      <div className="action-items-list">
                        {pendingActions.map((action, pos) => (
                          <div key={`action-${pos}`} className="action-item-details">
                            <div className="action-type-badge">
                              {action.action.toUpperCase()} {action.category.slice(0, -1).toUpperCase()}
                            </div>
                            <div className="action-data-grid">
                              {Object.entries(action.data).map(([key, value]) => {
                                if (key === 'id' || value === null || value === '' || key === 'datetime_start_local' || key === 'datetime_end_local') return null;
                                return (
                                  <div key={key} className={`action-data-field ${key === 'notes' || key === 'title' ? 'full-width' : ''}`}>
                                    <span className="field-label">{key.replace(/_/g, ' ')}</span>
                                    <span className="field-value">{formatActionValue(key, value)}</span>
                                  </div>
                                );
                              })}
                            </div>
                            <button type="button" 
                              className="primary-button small approve-btn" 
                              onClick={() => approveAction(action)}
                            >
                              Approve
                            </button>
                          </div>
                        ))}
                      </div>

                      <div className="action-review-footer">
                        <button type="button" className="ghost-button small deny-btn" onClick={denyAction}>
                          Cancel All
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {calendarConnectRequired && (
                  <div className="message-row assistant">
                    <div className="message-bubble action-review-card calendar-connect-notice">
                      <h4 className="action-review-title">Connect your calendar</h4>
                      <p className="action-review-subtitle">
                        Connect your Google or Outlook calendar to save events from calls.
                      </p>
                      <div className="action-review-footer">
                        <button
                          type="button"
                          className="primary-button small"
                          onClick={() => navigate('/settings')}
                        >
                          Connect
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <form className="assistant-input-area" onSubmit={handleSend}>
            {currentStatus && (
              <div className="assistant-status-indicator">
                <span className="status-text">
                  {currentStatus.replace(/\.\.\.$/, '')}
                  <span className="status-dots">
                    <span>.</span><span>.</span><span>.</span>
                  </span>
                </span>
              </div>
            )}
            <textarea
              ref={textareaRef}
              placeholder="Type a message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              rows={1}
            />
            <button type="submit" className="primary-button" disabled={!input.trim() || isLoading}>
              Send
            </button>
          </form>
        </div>
      </main>
    </div>
  )
}

export default AssistantView
